import React, { useLayoutEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import { Box, Typography } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import {
    INTEGRATION_CARD_HEIGHT,
    INTEGRATION_CARD_WIDTH,
    INTEGRATION_CARD_WIDTH_WITH_CATEGORIES,
    INTEGRATION_CARD_GUTTER,
} from '@thefront/pandipackV2'
import { IntegrationCard } from './integrationCardUtils'

const ARROW_WIDTH = 30
const ARROW_GUTTER = 10
const ARROWS_TOTAL = 2 * (ARROW_WIDTH + ARROW_GUTTER)

const CustomArrow = ({ className, onClick, direction }) => (
    <Box
        className={className}
        sx={{
            height: `${INTEGRATION_CARD_HEIGHT}px !important`,
            width: `${ARROW_WIDTH}px !important`,
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            backgroundColor: 'rgb(244, 244, 244) !important',
        }}
        onClick={onClick}
    >
        {direction === 'prev' ? (
            <ArrowBackIosNewIcon sx={{ color: 'black' }} />
        ) : (
            <ArrowForwardIosIcon sx={{ color: 'black' }} />
        )}
    </Box>
)

function useContainerWidth() {
    const ref = useRef(null)
    const [width, setWidth] = useState(0)

    useLayoutEffect(() => {
        const el = ref.current
        if (!el) return undefined
        const update = () => setWidth(el.clientWidth)
        update()

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update)
            return () => window.removeEventListener('resize', update)
        }
        const ro = new ResizeObserver(update)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    return [ref, width]
}

function IntegrationCarousel({ integrations, marketplaceSettings }) {
    const sliderTitle = marketplaceSettings?.carouselTitle ?? 'Featured'
    const outerWidth = marketplaceSettings.carouselWidth ?? 1
    const cardWidth = marketplaceSettings.hasCategories
        ? INTEGRATION_CARD_WIDTH_WITH_CATEGORIES
        : INTEGRATION_CARD_WIDTH
    const slotWidth = cardWidth + INTEGRATION_CARD_GUTTER

    const [measureRef, measuredWidth] = useContainerWidth()
    const fitsWithoutArrows = Math.floor(measuredWidth / slotWidth)
    const allFit = integrations.length <= fitsWithoutArrows
    const fits = allFit
        ? fitsWithoutArrows
        : Math.max(1, Math.floor((measuredWidth - ARROWS_TOTAL) / slotWidth))
    const slidesToShow = Math.min(integrations.length, fits)
    if (slidesToShow === 0) {
        return null
    }
    // When every card fits, infinite/arrows are pointless
    const sliderMaxWidth =
        slidesToShow * slotWidth + (allFit ? 0 : ARROWS_TOTAL)

    return (
        <Box
            sx={{
                width: outerWidth,
                padding: '40px 40px 80px',
            }}
        >
            <Typography
                sx={{
                    lineHeight: '48px',
                    fontSize: '40px',
                    textAlign: 'center',
                    paddingBottom: '40px',
                }}
            >
                {sliderTitle}
            </Typography>
            <Box ref={measureRef} sx={{ width: '100%' }}>
                {measuredWidth > 0 && (
                    <Box
                        sx={{
                            maxWidth: `${sliderMaxWidth}px`,
                            mx: 'auto',
                            '.slick-slide': {
                                display: 'flex !important',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '20px 0',
                                boxSizing: 'border-box',
                            },
                            '.slick-next::before, .slick-prev::before': {
                                content: 'none',
                            },
                        }}
                    >
                        <Slider
                            key={`${slidesToShow}-${allFit}`}
                            infinite={!allFit}
                            arrows={!allFit}
                            slidesToScroll={1}
                            slidesToShow={slidesToShow}
                            prevArrow={<CustomArrow direction="prev" />}
                            nextArrow={<CustomArrow direction="next" />}
                        >
                            {integrations.map((integration) => (
                                <IntegrationCard
                                    key={integration.id}
                                    integration={integration}
                                />
                            ))}
                        </Slider>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default IntegrationCarousel
